import { createApi } from '@reduxjs/toolkit/query/react';
import { ExamType } from 'core';

import { axiosBaseQuery } from '../axiosBaseQuery';

// /basket/examoption

export interface Basket {
  basketItems: BasketItem[];
  priceValidTo: string;
  isLastSessionStage: boolean;
}

export interface ComponentDetail {
  name: string;
  sessionTime: string;
  examDate: Date;
}

export interface BasketItem {
  $$hashKey?: string;
  awardingBody?: string;
  awardingBodyId: number;
  centre?: unknown;
  code: string;
  componentDetails: ComponentDetail[];
  contactName?: string;
  currency: string;
  district?: string;
  districtExamId?: number;
  districtId?: number;
  email?: string;
  examOptionId: number;
  examSyllabusId?: number;
  fee: number;
  feeDefined?: unknown;
  feeDiscountAmount?: number;
  groupAwardPreviousCandidateNumber?: unknown;
  groupAwardPreviousCenterNumber?: unknown;
  isCheckpointSession?: boolean;
  isGroupAward?: boolean;
  isGroupAwardPreviousDataSet?: boolean;
  milestoneDate?: string;
  name: string;
  qualificationLevel: string;
  qualificationLevelId?: number;
  retake?: BasketItemRetake | null;
  retakeSelected?: boolean;
  sessionId?: number;
  sessionMileStoneId: number;
  sessionMileStoneTypeId?: number;
  sessionName?: string;
  sessionStageId?: number;
  syllabusName: string;
  teachingGroup?: unknown;
  webUrl?: unknown;
  isCarryForward: boolean;
  isCashin: boolean;
}

export interface BasketItemRetake {
  type: ExamType;
  previousCenterNumber: string;
  previousCandidateNumber: string;
}

export interface TeachingGroupGetRequest {
  sessionId: number;
  productGroupSubSystemId: number;
  examSyllabusCode: string;
}

export const teachingGroupApi = createApi({
  reducerPath: 'teachingGroupApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api/api',
  }),
  endpoints: (builder) => ({
    getTeachingGroup: builder.query<Array<string>, TeachingGroupGetRequest>({
      query: (request) => ({
        url: '/teachingGroup',
        method: 'get',
        params: request,
      }),
    }),
  }),
});

export const basketApi = createApi({
  reducerPath: 'basketApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api/api',
  }),
  endpoints: (builder) => ({
    postBasketItem: builder.query<Basket, BasketParams>({
      query: ({ basketExamOptions, ...rest }) => ({
        url: `/basket/examoption`,
        method: 'post',
        data: {
          basketExamOptions: convertBasketExamOptions(basketExamOptions),
          ...rest,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (basket: BasketResponse) => ({
        basketItems: mapBasketResponse(basket.BasketItems),
        priceValidTo: basket.PriceValidTo,
        isLastSessionStage: basket.IsLastSessionStage,
      }),
    }),
    refreshPrices: builder.query<
      DistrictPrice[],
      { basketItems: BasketItem[]; candidateId?: number | null; sessionId?: number }
    >({
      query: (data) => ({
        url: `/basket/refreshPrices`,
        method: 'post',
        data: {
          BasketExamOptions: convertBasketExamOptions(data.basketItems),
          CandidateId: data.candidateId,
          SessionId: data.sessionId,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (res: DistrictPriceResponse[]) =>
        res.map((x) => ({ districtExamId: x.DistrictExamId, fee: x.Fee })),
    }),
  }),
});

export const { useLazyPostBasketItemQuery, useLazyRefreshPricesQuery } = basketApi;
export const { useLazyGetTeachingGroupQuery } = teachingGroupApi;

function convertBasketExamOptions(basketExamOptions: BasketItem[]) {
  return basketExamOptions.map((item) => ({
    $$hashKey: item.$$hashKey,
    AwardingBody: item.awardingBody,
    AwardingBodyId: item.awardingBodyId,
    Centre: item.centre,
    Code: item.code,
    ComponentDetails: item.componentDetails?.map((x) => ({
      Name: x.name,
      SessionTime: x.sessionTime,
      ExamDate: x.examDate,
    })),
    ContactName: item.contactName,
    Currency: item.currency,
    District: item.district,
    DistrictExamId: item.districtExamId,
    DistrictId: item.districtId,
    Email: item.email,
    ExamOptionId: item.examOptionId,
    ExamSyllabusId: item.examSyllabusId,
    Fee: item.fee,
    FeeDefined: item.feeDefined,
    FeeDiscountAmount: item.feeDiscountAmount,
    GroupAwardPreviousCandidateNumber: item.groupAwardPreviousCandidateNumber,
    GroupAwardPreviousCenterNumber: item.groupAwardPreviousCenterNumber,
    IsCheckpointSession: item.isCheckpointSession,
    IsGroupAward: item.isGroupAward,
    IsGroupAwardPreviousDataSet: item.isGroupAwardPreviousDataSet,
    MilestoneDate: item.milestoneDate,
    Name: item.name,
    QualificationLevel: item.qualificationLevel,
    QualificationLevelId: item.qualificationLevelId,
    Retake: item.retake,
    RetakeSelected: item.retakeSelected,
    SessionId: item.sessionId,
    SessionMileStoneId: item.sessionMileStoneId,
    SessionMileStoneTypeId: item.sessionMileStoneTypeId,
    SessionName: item.sessionName,
    SessionStageId: item.sessionStageId,
    SyllabusName: item.syllabusName,
    TeachingGroup: item.teachingGroup,
    WebUrl: item.webUrl,
    IsCarryForward: item.isCarryForward,
    IsCashin: item.isCashin,
  }));
}

function mapBasketResponse(basketItems: BasketItemResponse[]) {
  return basketItems.map((item) => ({
    $$hashKey: item.$$hashKey,
    awardingBody: item.AwardingBody,
    awardingBodyId: item.AwardingBodyId,
    centre: item.Centre,
    code: item.Code,
    componentDetails: item.ComponentDetails?.map((x) => ({
      name: x.Name,
      sessionTime: x.SessionTime,
      examDate: x.ExamDate,
    })),
    contactName: item.ContactName,
    currency: item.Currency,
    district: item.District,
    districtExamId: item.DistrictExamId,
    districtId: item.DistrictId,
    email: item.Email,
    examOptionId: item.ExamOptionId,
    examSyllabusId: item.ExamSyllabusId,
    fee: item.Fee,
    feeDefined: item.FeeDefined,
    feeDiscountAmount: item.FeeDiscountAmount,
    groupAwardPreviousCandidateNumber: item.GroupAwardPreviousCandidateNumber,
    groupAwardPreviousCenterNumber: item.GroupAwardPreviousCenterNumber,
    isCheckpointSession: item.IsCheckpointSession,
    isGroupAward: item.IsGroupAward,
    isGroupAwardPreviousDataSet: item.IsGroupAwardPreviousDataSet,
    milestoneDate: item.MilestoneDate,
    name: item.Name,
    qualificationLevel: item.QualificationLevel,
    qualificationLevelId: item.QualificationLevelId,
    retake: item.Retake
      ? {
          type: item.Retake.Type,
          previousCenterNumber: item.Retake.PreviousCenterNumber,
          previousCandidateNumber: item.Retake.PreviousCandidateNumber,
        }
      : null,
    retakeSelected: item.RetakeSelected,
    sessionId: item.SessionId,
    sessionMileStoneId: item.SessionMileStoneId,
    sessionMileStoneTypeId: item.SessionMileStoneTypeId,
    sessionName: item.SessionName,
    sessionStageId: item.SessionStageId,
    syllabusName: item.SyllabusName,
    teachingGroup: item.TeachingGroup,
    webUrl: item.WebUrl,
    isCarryForward: item.IsCarryForward,
    isCashin: item.IsCashin,
  }));
}

export interface ComponentDetailResponse {
  Name: string;
  SessionTime: string;
  ExamDate: Date;
}

interface BasketResponse {
  BasketItems: BasketItemResponse[];
  PriceValidTo: string;
  IsLastSessionStage: boolean;
}

interface BasketItemResponse {
  $$hashKey?: string;
  AwardingBody?: string;
  AwardingBodyId: number;
  Centre?: unknown;
  Code: string;
  ComponentDetails: ComponentDetailResponse[];
  ContactName?: string;
  Currency: string;
  District?: string;
  DistrictExamId?: number;
  DistrictId?: number;
  Email?: string;
  ExamOptionId: number;
  ExamSyllabusId?: number;
  Fee: number;
  FeeDefined?: unknown;
  FeeDiscountAmount?: number;
  GroupAwardPreviousCandidateNumber?: unknown;
  GroupAwardPreviousCenterNumber?: unknown;
  IsCheckpointSession?: boolean;
  IsGroupAward?: boolean;
  IsGroupAwardPreviousDataSet?: boolean;
  MilestoneDate?: string;
  Name: string;
  QualificationLevel: string;
  QualificationLevelId?: number;
  Retake?: BasketItemRetakeResponse | null;
  RetakeSelected?: boolean;
  SessionId?: number;
  SessionMileStoneId: number;
  SessionMileStoneTypeId?: number;
  SessionName?: string;
  SessionStageId?: number;
  SyllabusName: string;
  TeachingGroup?: unknown;
  WebUrl?: unknown;
  IsCarryForward: boolean;
  IsCashin: boolean;
}

interface BasketParams {
  basketExamOptions: BasketItem[];
  addedExamOptionId: number;
  awardingBodyId: number;
  districtId: number;
  originSubSystemShortCode: string;
  sessionId: number;
  subSystemShortCode: string;
  retakeSelected: boolean;
  teachingGroup?: string;
  preparationCentreCode?: string;
}

export interface BasketItemRetakeResponse {
  Type: ExamType;
  PreviousCenterNumber: string;
  PreviousCandidateNumber: string;
}

export interface DistrictPriceResponse {
  DistrictExamId: number;
  Fee: number;
}

export interface DistrictPrice {
  districtExamId: number;
  fee: number;
}
